/**
 * Gets date format information for a timezone using browser's Intl API
 * Simplified for PrimeVue Calendar component usage
 */

import { DATE_FORMATS } from './constants'

export type DateFormatInfo = {
  locale: string
  dateFormat: string // PrimeVue format (e.g., 'dd/mm/yy', 'mm/dd/yy')
  shortDateFormat: string // Short version (e.g., 'D/M/YY')
}

/**
 * Detects the date format pattern for a given timezone
 * Returns format compatible with PrimeVue Calendar
 */
export function getDateFormatForTimezone(timezone: string): DateFormatInfo {
  const locale = inferLocaleFromTimezone(timezone)

  try {
    // Create a formatter for the locale/timezone
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })

    // Use formatToParts to detect the order and separator
    const parts = formatter.formatToParts(new Date(2023, 0, 15)) // Jan 15, 2023

    let order = ''
    let separator = '/'

    for (const part of parts) {
      if (part.type === 'month') {
        order += 'M'
      } else if (part.type === 'day') {
        order += 'D'
      } else if (part.type === 'year') {
        order += 'Y'
      } else if (part.type === 'literal' && part.value.trim()) {
        // Remove RTL/LTR directional markers that can appear in some locales
        separator = part.value
          .trim()
          .replace(/[\u200E\u200F\u202D\u202E\u061C‏‎]/g, '')
      }
    }

    // Generate PrimeVue-compatible format strings
    const formatParts = []
    const shortFormatParts = []

    for (const char of order) {
      if (char === 'M') {
        formatParts.push('mm')
        shortFormatParts.push('M')
      } else if (char === 'D') {
        formatParts.push('dd')
        shortFormatParts.push('D')
      } else if (char === 'Y') {
        formatParts.push('yy')
        shortFormatParts.push('YY')
      }
    }

    const dateFormat = formatParts.join(separator)
    const shortDateFormat = shortFormatParts.join(separator)

    return {
      locale,
      dateFormat,
      shortDateFormat,
    }
  } catch (_error) {
    // Fallback for invalid timezones
    return {
      locale: 'en-US',
      dateFormat: DATE_FORMATS.US,
      shortDateFormat: DATE_FORMATS.US_SHORT,
    }
  }
}

/**
 * Gets regional fallback locale for a timezone based on patterns and region prefixes
 */
function getRegionalFallback(timezone: string): string | null {
  // Check specific patterns first
  if (timezone.includes('Mexico') || timezone.includes('Tijuana')) {
    return 'es-MX'
  }
  if (timezone.includes('Sao_Paulo') || timezone.includes('Brazil')) {
    return 'pt-BR'
  }
  if (timezone.includes('Argentina')) {
    return 'es-AR'
  }
  if (timezone.includes('Canada')) {
    return 'en-CA'
  }

  // Then check region prefixes
  const region = timezone.split('/')[0]

  switch (region) {
    case 'America': {
      return 'en-US'
    }
    case 'Europe': {
      return 'en-GB'
    }
    case 'Asia': {
      return 'en-SG'
    }
    case 'Australia': {
      return 'en-AU'
    }
    case 'Pacific': {
      return timezone.includes('Auckland') ? 'en-NZ' : 'en-US'
    }
    case 'Africa': {
      return 'en-ZA'
    }
    default: {
      return null
    }
  }
}

/**
 * Infers the most appropriate locale from a timezone identifier
 */
function inferLocaleFromTimezone(timezone: string): string {
  // Map of common timezones to their locales
  const timezoneToLocale: Record<string, string> = {
    // Americas
    'America/New_York': 'en-US',
    'America/Chicago': 'en-US',
    'America/Denver': 'en-US',
    'America/Los_Angeles': 'en-US',
    'America/Phoenix': 'en-US',
    'America/Juneau': 'en-US',
    'Pacific/Honolulu': 'en-US',
    'America/Halifax': 'en-CA',
    'America/Regina': 'en-CA',
    'America/Toronto': 'en-CA',
    'America/Vancouver': 'en-CA',
    'America/Mexico_City': 'es-MX',
    'America/Tijuana': 'es-MX',
    'America/Mazatlan': 'es-MX',
    'America/Chihuahua': 'es-MX',
    'America/Monterrey': 'es-MX',
    'America/Guatemala': 'es-GT',
    'America/Sao_Paulo': 'pt-BR',
    'America/Argentina/Buenos_Aires': 'es-AR',
    'America/Santiago': 'es-CL',
    'America/Bogota': 'es-CO',
    'America/Lima': 'es-PE',
    'America/Caracas': 'es-VE',
    'America/La_Paz': 'es-BO',
    'America/Montevideo': 'es-UY',

    // Europe
    'Europe/London': 'en-GB',
    'Europe/Dublin': 'en-IE',
    'Europe/Paris': 'fr-FR',
    'Europe/Berlin': 'de-DE',
    'Europe/Rome': 'it-IT',
    'Europe/Madrid': 'es-ES',
    'Europe/Amsterdam': 'nl-NL',
    'Europe/Brussels': 'fr-BE',
    'Europe/Vienna': 'de-AT',
    'Europe/Zurich': 'de-CH',
    'Europe/Stockholm': 'sv-SE',
    'Europe/Copenhagen': 'da-DK',
    'Europe/Oslo': 'nb-NO',
    'Europe/Helsinki': 'fi-FI',
    'Europe/Warsaw': 'pl-PL',
    'Europe/Prague': 'cs-CZ',
    'Europe/Budapest': 'hu-HU',
    'Europe/Athens': 'el-GR',
    'Europe/Bucharest': 'ro-RO',
    'Europe/Sofia': 'bg-BG',
    'Europe/Moscow': 'ru-RU',
    'Europe/Kyiv': 'uk-UA',
    'Europe/Kiev': 'uk-UA',
    'Europe/Minsk': 'be-BY',
    'Europe/Riga': 'lv-LV',
    'Europe/Tallinn': 'et-EE',
    'Europe/Vilnius': 'lt-LT',
    'Europe/Belgrade': 'sr-RS',
    'Europe/Zagreb': 'hr-HR',
    'Europe/Ljubljana': 'sl-SI',
    'Europe/Sarajevo': 'bs-BA',
    'Europe/Skopje': 'mk-MK',
    'Europe/Bratislava': 'sk-SK',

    // Asia
    'Asia/Tokyo': 'ja-JP',
    'Asia/Seoul': 'ko-KR',
    'Asia/Shanghai': 'zh-CN',
    'Asia/Hong_Kong': 'zh-HK',
    'Asia/Taipei': 'zh-TW',
    'Asia/Singapore': 'en-SG',
    'Asia/Kuala_Lumpur': 'ms-MY',
    'Asia/Bangkok': 'th-TH',
    'Asia/Jakarta': 'id-ID',
    'Asia/Rangoon': 'my-MM',
    'Asia/Kolkata': 'en-IN',
    'Asia/Karachi': 'ur-PK',
    'Asia/Dubai': 'ar-AE',
    'Asia/Riyadh': 'ar-SA',
    'Asia/Tehran': 'fa-IR',
    'Asia/Jerusalem': 'he-IL',

    // Australia/Pacific
    'Australia/Sydney': 'en-AU',
    'Australia/Melbourne': 'en-AU',
    'Australia/Brisbane': 'en-AU',
    'Australia/Perth': 'en-AU',
    'Australia/Adelaide': 'en-AU',
    'Australia/Darwin': 'en-AU',
    'Australia/Hobart': 'en-AU',
    'Pacific/Auckland': 'en-NZ',

    // Africa
    'Africa/Cairo': 'ar-EG',
    'Africa/Johannesburg': 'en-ZA',
    'Africa/Lagos': 'en-NG',
    'Africa/Nairobi': 'sw-KE',
    'Africa/Casablanca': 'ar-MA',

    // Other
    'Etc/UTC': 'en-US',
    'Atlantic/Azores': 'pt-PT',
    'America/Godthab': 'kl-GL',
  }

  // Check for exact match first
  if (timezoneToLocale[timezone]) {
    return timezoneToLocale[timezone]
  }

  // Use regional fallback logic
  const fallbackLocale = getRegionalFallback(timezone)
  if (fallbackLocale) {
    return fallbackLocale
  }

  // Ultimate fallback
  return 'en-US'
}
